import React, { useEffect, useMemo, useState } from 'react';
import { authFetch } from '../utils/authFetch';

function formatDateTime(value) {
  if (!value) return '-';

  try {
    return new Date(value).toLocaleString();
  } catch {
    return '-';
  }
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '-';

  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);

  return n.toFixed(2);
}

function getRefLabel(ref, fallback = '-') {
  if (!ref) return fallback;

  if (typeof ref === 'string') return ref;

  return ref.name || ref.nome || ref.username || ref.matricola || fallback;
}

function getEventTypeLabel(type) {
  if (type === 'low_temperature') return 'Temperatura troppo bassa';
  if (type === 'high_temperature') return 'Temperatura troppo alta';
  return 'Allarme temperatura';
}

export default function ClientAlarmPopup() {
  const [events, setEvents] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loadingActionId, setLoadingActionId] = useState(null);
  const [error, setError] = useState('');

  const loadActiveAlarms = async () => {
    try {
      setError('');

      const res = await authFetch('/api/alarms/events?status=active');
      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(data?.message || 'Errore recupero allarmi attivi');
      }

      const list = Array.isArray(data?.events) ? data.events : [];

      setEvents(list);

      if (list.length > 0) {
        setIsOpen(true);
      }
    } catch (err) {
      console.error('Errore popup allarmi:', err);
      setError(err?.message || 'Impossibile recuperare gli allarmi.');
    }
  };

  useEffect(() => {
    loadActiveAlarms();

    const timer = window.setInterval(() => {
      loadActiveAlarms();
    }, 30000);

    return () => window.clearInterval(timer);
  }, []);

  const primaryEvent = events[0];

  const otherEventsCount = useMemo(() => {
    return Math.max(0, events.length - 1);
  }, [events.length]);

  const acknowledgeEvent = async (event) => {
    if (!event?._id) return;

    try {
      setLoadingActionId(event._id);
      setError('');

      const res = await authFetch(`/api/alarms/events/${event._id}/ack`, {
        method: 'POST',
      });

      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(data?.message || 'Errore presa in carico allarme');
      }

      await loadActiveAlarms();
    } catch (err) {
      console.error('Errore presa in carico allarme:', err);
      setError(err?.message || 'Impossibile prendere in carico l’allarme.');
    } finally {
      setLoadingActionId(null);
    }
  };

  if (!primaryEvent) {
    return null;
  }

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-5 right-5 z-40 rounded-full bg-red-600 px-4 py-3 text-sm font-bold text-white shadow-2xl hover:bg-red-700 transition"
        >
          {events.length} allarme{events.length === 1 ? '' : 'i'}
        </button>
      )}

      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-3 pb-4 sm:items-center sm:p-6">
          <div className="w-full max-w-xl overflow-hidden rounded-3xl border border-red-200 bg-white shadow-2xl">
            <div className="bg-gradient-to-r from-red-700 to-red-600 px-5 py-4 text-white">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-red-100">
                    Allarme attivo
                  </p>

                  <h2 className="mt-1 text-xl font-bold">
                    {getEventTypeLabel(primaryEvent.type)}
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="rounded-full bg-white/15 px-3 py-1 text-sm font-bold hover:bg-white/25 transition"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-5">
              {error && (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div className="rounded-2xl border border-red-100 bg-red-50 p-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-red-700">
                      Reparto
                    </p>
                    <p className="mt-1 text-sm font-bold text-slate-900">
                      {getRefLabel(primaryEvent.departmentRef)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-red-700">
                      Macchina
                    </p>
                    <p className="mt-1 text-sm font-bold text-slate-900">
                      {getRefLabel(primaryEvent.machineRef)}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-red-700">
                      Sonda
                    </p>
                    <p className="mt-1 text-sm font-bold text-slate-900">
                      {primaryEvent.sensor_label || primaryEvent.sensor_id}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-red-700">
                      Valore rilevato
                    </p>
                    <p className="mt-1 text-lg font-black text-red-700">
                      {formatValue(primaryEvent.value)} {primaryEvent.unit || 'C'}
                    </p>
                  </div>
                </div>

                <div className="mt-4 rounded-xl bg-white px-3 py-3 text-sm text-slate-700">
                  <p>
                    Range impostato:{' '}
                    <strong>
                      {formatValue(primaryEvent.min_value)} / {formatValue(primaryEvent.max_value)} {primaryEvent.unit || 'C'}
                    </strong>
                  </p>

                  <p className="mt-1">
                    Allarme generato:{' '}
                    <strong>{formatDateTime(primaryEvent.triggered_at)}</strong>
                  </p>
                </div>
              </div>

              {otherEventsCount > 0 && (
                <p className="mt-3 text-sm font-semibold text-red-700">
                  Sono presenti anche altri {otherEventsCount} allarmi attivi.
                </p>
              )}

              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  disabled={loadingActionId === primaryEvent._id}
                  onClick={() => acknowledgeEvent(primaryEvent)}
                  className="flex-1 rounded-xl bg-amber-500 px-4 py-3 text-sm font-bold text-white hover:bg-amber-600 transition disabled:opacity-60"
                >
                  {loadingActionId === primaryEvent._id
                    ? 'Attendere...'
                    : 'Prendi in carico'}
                </button>

                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="flex-1 rounded-xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-800 hover:bg-slate-200 transition"
                >
                  Chiudi
                </button>
              </div>

              <p className="mt-4 text-xs text-slate-500 leading-5">
                La presa in carico segnala che l’allarme è stato visto. La risoluzione definitiva resta gestita dalla segreteria/gestione quando la temperatura rientra o il problema viene chiuso.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}